/* =============================================================================
   VIEWS / UI / DIALOGS — styled In-App-Confirm & -Prompt (S190)
   =============================================================================
   Projekt-Prinzip (Jonas 2026-07-01): NIE native window.prompt/confirm/alert.
   Diese Promise-basierten Helfer ersetzen sie systemweit durch styled Overlays
   (Dark-Mode-Tokens). Rein imperativ aufgebaut (createElement + addEventListener,
   KEINE inline-onclick → CSP-sauber, keine window.func-Handler). Fokus-Management,
   Esc = Abbrechen, Enter = Bestätigen (wenn erlaubt), Außenklick = Abbrechen.

   API:
     await styledConfirm({ title, body, danger, confirmLabel, cancelLabel,
                           reasonField, reasonLabel, reasonPlaceholder, challenge })
       → { confirmed: boolean, reason: string|null }
     await styledPrompt({ title, label, value, placeholder, confirmLabel,
                          cancelLabel, required })
       → string | null   (null = abgebrochen)
============================================================================ */

import {
  confirmConfig, promptConfig, normalizeReason, canConfirm, promptValueValid
} from '../../core/dialog-logic.js';

function _mkEl(tag, cls, text) {
  const el = document.createElement(tag);
  if (cls) el.className = cls;
  if (text != null) el.textContent = text;
  return el;
}

/**
 * S249 (B3) — Reste aufräumen, bevor ein neuer Dialog aufgeht.
 *
 * Entfernt werden ausschliesslich Overlays, deren `close()` bereits gelaufen ist
 * (Markierung `data-sdlg-closed`) — ein absichtlich gestapelter Dialog bleibt
 * unangetastet. Das ist die zweite Linie hinter der Reihenfolge-Korrektur unten:
 * ein Rest-Overlay liegt mit `z-index: 9500` über allem und schluckt Klicks,
 * auch wenn es durchsichtig ist.
 */
function _sweepClosedOverlays() {
  if (typeof document === 'undefined') return;
  document.querySelectorAll('.sdlg-overlay[data-sdlg-closed]').forEach(el => {
    if (el.parentNode) el.parentNode.removeChild(el);
  });
}

/** Gemeinsames Overlay-Gerüst; ruft onMount(panel, resolve, close) und liefert ein
 *  Promise, das über resolve(value) aufgelöst wird. Räumt Listener sauber ab. */
function _openDialog(onMount) {
  return new Promise(resolve => {
    if (typeof document === 'undefined') { resolve(undefined); return; }
    _sweepClosedOverlays();

    const overlay = _mkEl('div', 'sdlg-overlay');
    const panel = _mkEl('div', 'sdlg-panel');
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    overlay.appendChild(panel);

    let done = false;
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); close(undefined); }
    };
    function close(value) {
      if (done) return;
      done = true;
      document.removeEventListener('keydown', onKey, true);
      // Markieren, BEVOR entfernt wird: hängt das Overlay (noch) nicht im
      // Dokument, greift der Sweep beim nächsten Dialog.
      overlay.setAttribute('data-sdlg-closed', '1');
      overlay.classList.remove('open');
      if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
      resolve(value);
    }
    overlay.addEventListener('mousedown', (e) => { if (e.target === overlay) close(undefined); });
    document.addEventListener('keydown', onKey, true);

    // S249 (B3) — ERST einhängen, DANN aufbauen. Vorher lief `onMount` auf einem
    // Knoten, der noch nicht im Dokument hing. Zwei Folgen:
    //   1. Ein `close()` aus `onMount` heraus fand kein `parentNode` — das
    //      Overlay wurde danach trotzdem angehängt und blieb für immer liegen.
    //      Genau dieser Rest lag im Belegungsplan über der Seite.
    //   2. `focus()` auf einem nicht eingehängten Element tut nichts: der Dialog
    //      ging ohne Fokus auf, Enter bestätigte erst nach einem Klick.
    document.body.appendChild(overlay);
    onMount(panel, close);
    if (!done) requestAnimationFrame(() => { if (!done) overlay.classList.add('open'); });
  });
}

/** Styled Bestätigungs-Dialog (Löschen/irreversibel). */
export function styledConfirm(opts) {
  const cfg = confirmConfig(opts);
  return _openDialog((panel, close) => {
    panel.classList.toggle('sdlg-danger', cfg.danger);
    panel.appendChild(_mkEl('h3', 'sdlg-title', cfg.title));
    if (cfg.body) {
      const p = _mkEl('p', 'sdlg-body');
      p.textContent = cfg.body;   // textContent = kein HTML-Inject
      panel.appendChild(p);
    }

    let reasonEl = null;
    if (cfg.reasonField) {
      const wrap = _mkEl('label', 'sdlg-field');
      wrap.appendChild(_mkEl('span', 'sdlg-field-label', cfg.reasonLabel));
      reasonEl = _mkEl('textarea', 'sdlg-input');
      reasonEl.rows = 2;
      reasonEl.placeholder = cfg.reasonPlaceholder;
      wrap.appendChild(reasonEl);
      panel.appendChild(wrap);
    }

    let challengeEl = null;
    if (cfg.challenge) {
      const wrap = _mkEl('label', 'sdlg-field');
      wrap.appendChild(_mkEl('span', 'sdlg-field-label', 'Zur Bestätigung tippen:'));
      challengeEl = _mkEl('input', 'sdlg-input');
      challengeEl.type = 'text';
      challengeEl.setAttribute('autocomplete', 'off');
      wrap.appendChild(challengeEl);
      panel.appendChild(wrap);
    }

    const row = _mkEl('div', 'sdlg-actions');
    const cancelBtn = _mkEl('button', 'btn btn-ghost', cfg.cancelLabel);
    cancelBtn.type = 'button';
    const okBtn = _mkEl('button', 'btn ' + (cfg.danger ? 'btn-danger' : 'btn-primary'), cfg.confirmLabel);
    okBtn.type = 'button';

    const state = { challengeInput: '' };
    const refresh = () => { okBtn.disabled = !canConfirm(cfg, state); };
    const confirmNow = () => {
      if (!canConfirm(cfg, state)) return;
      close({ confirmed: true, reason: reasonEl ? normalizeReason(reasonEl.value) : null });
    };
    if (challengeEl) challengeEl.addEventListener('input', () => { state.challengeInput = challengeEl.value; refresh(); });
    cancelBtn.addEventListener('click', () => close({ confirmed: false, reason: null }));
    okBtn.addEventListener('click', confirmNow);
    panel.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !(reasonEl && e.target === reasonEl)) { e.preventDefault(); confirmNow(); }
    });

    row.appendChild(cancelBtn);
    row.appendChild(okBtn);
    panel.appendChild(row);
    refresh();
    (challengeEl || reasonEl || okBtn).focus();
  }).then(v => v || { confirmed: false, reason: null });
}

/** Styled Einzel-Eingabe (Create/Rename/Value) — ersetzt window.prompt. */
export function styledPrompt(opts) {
  const cfg = promptConfig(opts);
  return _openDialog((panel, close) => {
    panel.appendChild(_mkEl('h3', 'sdlg-title', cfg.title));
    const wrap = _mkEl('label', 'sdlg-field');
    if (cfg.label) wrap.appendChild(_mkEl('span', 'sdlg-field-label', cfg.label));
    const input = _mkEl('input', 'sdlg-input');
    input.type = 'text';
    input.value = cfg.value;
    input.placeholder = cfg.placeholder;
    input.setAttribute('autocomplete', 'off');
    wrap.appendChild(input);
    panel.appendChild(wrap);

    const row = _mkEl('div', 'sdlg-actions');
    const cancelBtn = _mkEl('button', 'btn btn-ghost', cfg.cancelLabel);
    cancelBtn.type = 'button';
    const okBtn = _mkEl('button', 'btn btn-primary', cfg.confirmLabel);
    okBtn.type = 'button';

    const refresh = () => { okBtn.disabled = !promptValueValid(cfg, input.value); };
    const submit = () => { if (promptValueValid(cfg, input.value)) close(input.value.trim()); };
    input.addEventListener('input', refresh);
    input.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } });
    cancelBtn.addEventListener('click', () => close(null));
    okBtn.addEventListener('click', submit);

    row.appendChild(cancelBtn);
    row.appendChild(okBtn);
    panel.appendChild(row);
    refresh();
    input.focus();
    input.select();
  }).then(v => (v === undefined ? null : v));
}

if (typeof window !== 'undefined') {
  Object.assign(window, { styledConfirm, styledPrompt });
}
